import { useEffect, useState, useCallback } from "react";
import { Users, MapPin, Clock, Loader2, RefreshCw, CheckCircle2, Circle } from "lucide-react";
import api from "@/lib/api";

const STATUS_DOT = { done: "bg-[#2EC4B6]", in_progress: "bg-[#FFC43D]", todo: "bg-[#d9d9d5]" };

export default function LeadTeamPanel() {
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    try { const r = await api.get("/lead/overview"); setData(r.data); } catch (e) {}
  }, []);
  useEffect(() => {
    load();
    const id = setInterval(load, 5000);
    return () => clearInterval(id);
  }, [load]);

  if (!data) return <div className="grid place-items-center py-16"><Loader2 className="w-7 h-7 animate-spin text-[var(--accent)]" /></div>;

  const s = data.summary;
  const stats = [
    { label: "Members", value: s.members },
    { label: "Completed", value: s.completed },
    { label: "Pending", value: s.pending },
    { label: "Check-ins today", value: s.checkins_today },
  ];

  return (
    <div data-testid="lead-team-panel" className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="label-mono text-[var(--text-2)] flex items-center gap-2"><Users className="w-4 h-4" /> Team dashboard</p>
          <h2 className="font-serif text-2xl mt-1">{data.team?.name}</h2>
        </div>
        <span className="text-[11px] text-[var(--text-2)] flex items-center gap-1"><RefreshCw className="w-3 h-3" /> live · every 5s</span>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {stats.map((st) => (
          <div key={st.label} data-testid={`lead-stat-${st.label.replace(/\s+/g, "-").toLowerCase()}`} className="border border-[var(--line)] bg-white p-4">
            <p className="text-3xl font-serif leading-none">{st.value}</p>
            <p className="text-xs text-[var(--text-2)] mt-1">{st.label}</p>
          </div>
        ))}
      </div>

      <div className="space-y-3">
        {data.members.length === 0 && <p className="text-sm text-[var(--text-2)]">No members on this team yet.</p>}
        {data.members.map((m) => (
          <div key={m.id} data-testid={`lead-member-${m.id}`} className="border border-[var(--line)] bg-white p-4">
            <div className="flex items-center justify-between gap-3">
              <p className="text-sm font-medium flex items-center gap-2">
                <span className={`w-2 h-2 rounded-full ${m.active ? "bg-[#2EC4B6]" : "bg-[#FF6B6B]"}`} />
                {m.name}{m.is_self && <span className="text-[10px] uppercase tracking-wider text-[var(--accent)]">You</span>}
              </p>
              <span className="text-xs text-[var(--text-2)]">{m.tasks.done}/{m.tasks.total} done</span>
            </div>

            <div className="mt-3 space-y-1.5">
              {m.task_list.length === 0 ? (
                <p className="text-xs text-[var(--text-2)]">No tasks assigned</p>
              ) : m.task_list.map((t) => (
                <div key={t.id} className="flex items-center gap-2 text-sm">
                  {t.status === "done" ? <CheckCircle2 className="w-4 h-4 text-[#2EC4B6] shrink-0" /> : <Circle className="w-4 h-4 text-[var(--text-2)] shrink-0" />}
                  <span className={`flex-1 truncate ${t.status === "done" ? "line-through text-[var(--text-2)]" : ""}`}>{t.title}</span>
                  <span className={`w-2 h-2 rounded-full ${STATUS_DOT[t.status] || STATUS_DOT.todo}`} />
                  {t.due_date && <span className="text-xs text-[var(--text-2)] font-mono shrink-0">{t.due_date}</span>}
                </div>
              ))}
            </div>

            {(m.recent_checkins || []).length > 0 && (
              <div className="mt-3 border-t border-[var(--line)] pt-2 space-y-1.5">
                <p className="text-[11px] uppercase tracking-wider text-[var(--text-2)]">Recent check-ins</p>
                {m.recent_checkins.map((c) => (
                  <div key={c.id} className="flex items-center gap-2 text-xs text-[var(--text-2)]">
                    <MapPin className="w-3 h-3 text-[var(--accent)] shrink-0" />
                    <span className="truncate flex-1">{c.task_title || "On-site"}{c.note ? ` — ${c.note}` : ""}</span>
                    <span className="font-mono flex items-center gap-1 shrink-0"><Clock className="w-3 h-3" /> {new Date(c.created_at).toLocaleString()}</span>
                    {c.photo_url && <a data-testid={`checkin-photo-${c.id}`} href={`${process.env.REACT_APP_BACKEND_URL}${c.photo_url}`} target="_blank" rel="noreferrer" className="shrink-0"><img src={`${process.env.REACT_APP_BACKEND_URL}${c.photo_url}`} alt="proof" className="w-7 h-7 object-cover rounded border border-[var(--line)]" /></a>}
                    {c.maps_url && <a href={c.maps_url} target="_blank" rel="noreferrer" className="text-[var(--accent)] hover:underline shrink-0">map</a>}
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
